import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { CardFormDialog } from "components/main/organizations/CardFormDialog";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useShallow } from "zustand/react/shallow";
import { Dialog } from "@/components/main/utils/Dialog";
import { DialogActions } from "@/components/main/utils/DialogActions";
import { Button } from "components/common/Button";
import { TextInput } from "components/common/TextInput";
import { SectionRule } from "components/common/SectionRule";
import { Spinner } from "components/common/Spinner";
import { Tooltip } from "components/common/Tooltip";
import { CopyButton } from "components/common/CopyButton";
import { MetaAddButton } from "components/main/datasets/DatasetMetaSection";
import { Badge } from "components/common/Badge";
import { Callout } from "components/common/Callout";
import { ImpactRow, plural } from "components/common/ImpactRow";
import { CriticalTableRow } from "components/main/utils/settings/account-deletion/CriticalTableRow";
import type { TableWithModel } from "components/main/utils/settings/account-deletion/useDeleteAccount";
import { MoveTableDialog } from "components/canvas/detailsSide/utils/ownership/MoveTableDialog";
import { EditableName } from "components/canvas/utils/EditableName";
import { mcpServerUrl } from "@/components/main/utils/tokens/mcpConnect";
import { TooltipButton } from "components/common/TooltipButton";
import { showNotificationGlobal } from "stores/useNotificationStore";
import {
  cancelOrgDeletion,
  deleteOrg,
  fetchLeaveOrgPreview,
  mintCardTransaction,
  fetchOrgLimits,
  getOrgDeletionAnalysis,
  leaveOrg,
  removeOrgImage,
  renameOrg,
  updateOrgImage,
  type LeaveOrgPreview,
  type Org,
  type OrgLimits,
  type OrgDeletionAnalysis,
  type OrgMember,
} from "@/lib/api/organizations";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import {
  IMAGE_ACCEPTED_TYPES,
  IMAGE_ACCEPT_STRING,
  IMAGE_MAX_FILE_SIZE,
  resizeImage,
} from "@/lib/images";
import { formatDayMonthYear } from "@/lib/dates";
import { GRACE_DAYS, purgeDate } from "@/lib/deletionGrace";
import { OrgMark } from "./OrgMark";
import { orgCreateRefusal } from "@/components/main/organizations/orgCreateRefusal";
import { useAuth } from "contexts/AuthProvider";

/// One shared width so the Danger row reads as a set of equals rather than
/// two buttons sized by their own label.
const DANGER_BUTTON_WIDTH = "w-32";

const message = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const roleLabel = (role?: string) =>
  role === "owner" ? "Owner" : role === "org_admin" ? "Admin" : "Member";

const isGovernorOf = (o: Org) => o.role === "owner" || o.role === "org_admin";

export const GeneralPanel: React.FC<{
  org: Org;
  members: OrgMember[];
}> = ({ org, members }) => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const {
    orgs,
    refreshOrgs,
    refreshSpaces,
    bumpImageVersion,
    switchOrg,
    activeOrgId,
  } = useOrgContextStore(
    useShallow((s) => ({
      orgs: s.orgs,
      refreshOrgs: s.refreshOrgs,
      refreshSpaces: s.refreshSpaces,
      bumpImageVersion: s.bumpImageVersion,
      switchOrg: s.switchOrg,
      activeOrgId: s.activeOrgId,
    })),
  );

  const [isEditingName, setIsEditingName] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leavePreview, setLeavePreview] = useState<LeaveOrgPreview | null>(
    null,
  );
  const [orgLimits, setOrgLimits] = useState<OrgLimits | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [analysis, setAnalysis] = useState<OrgDeletionAnalysis | null>(null);
  const [isCancellingDeletion, setIsCancellingDeletion] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const companyOrgs = orgs.filter((o) => !o.is_personal);
  // Company orgs are not open to everyone yet, so the door is hidden rather
  // than disabled — a disabled button with an explanation advertises the
  // feature to every account that opens its settings. UI-only on purpose: the
  // API stays open, so the e2e scenarios and any script keep working.
  const canCreateOrg = isAdmin;
  // Only the personal org shows the door, so only it needs the headroom.
  useEffect(() => {
    if (!org.is_personal) return;
    void fetchOrgLimits()
      .then(setOrgLimits)
      .catch(() => setOrgLimits(null));
  }, [org.is_personal]);
  // The org-scoped MCP alias — connections made through it are confined to
  // this org. Shared builder so local dev shows the proxy origin the MCP
  // server actually serves, same as AccessConnectPanel.
  const mcpEndpointUrl = mcpServerUrl(org.org_id);
  const isOwner = org.is_personal || org.role === "owner";
  const isGovernor = org.role === "owner" || org.role === "org_admin";

  // The pre-flight is what turns "delete this org" into an informed decision,
  // so the dialog opens on its result rather than racing it.
  const [movingTable, setMovingTable] = useState<TableWithModel | null>(null);

  const openDeleteDialog = async () => {
    setAnalysis(null);
    setDeleteConfirmName("");
    setDeleteOpen(true);
    try {
      setAnalysis(await getOrgDeletionAnalysis(org.org_id));
    } catch {
      // Leave `analysis` null — the dialog falls back to generic wording
      // rather than blocking the door on a failed count.
    }
  };
  // `critical_tables` is only a count; the identities are already in the
  // per-model breakdown, which is what the rescue needs.
  const criticalTables = useMemo<TableWithModel[]>(
    () =>
      (analysis?.models ?? []).flatMap((m) =>
        m.tables
          .filter((t) => t.usage_status === "Critical")
          .map((t) => ({
            ...t,
            model_id: m.model_id,
            model_name: m.model_name,
          })),
      ),
    [analysis],
  );

  const owners = members.filter((m) => m.role === "owner");
  const canGovern =
    org.is_personal || org.role === "owner" || org.role === "org_admin";
  // `EditableName.commit` runs synchronously and reads the return value, so
  // the PUT is fired off and its result handled later.
  const commitName = (name: string): boolean => {
    const trimmed = name.trim();
    if (trimmed === org.name) return true;
    if (trimmed.length < 2) {
      showNotificationGlobal(
        "Organization name must be at least 2 characters",
        "error",
      );
      return false;
    }
    void renameOrg(org.org_id, trimmed)
      .then(() => refreshOrgs())
      .catch((error: unknown) =>
        showNotificationGlobal(message(error, "Rename failed"), "error"),
      );
    return true;
  };

  const uploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (
      !IMAGE_ACCEPTED_TYPES.includes(
        file.type as (typeof IMAGE_ACCEPTED_TYPES)[number],
      )
    ) {
      showNotificationGlobal("Upload a JPEG, PNG, WebP or SVG image", "error");
      return;
    }
    if (file.size > IMAGE_MAX_FILE_SIZE) {
      showNotificationGlobal("Image must be under 1 MB", "error");
      return;
    }
    setIsUploading(true);
    try {
      const dataUrl = await resizeImage(file);
      await updateOrgImage(org.org_id, dataUrl);
      await refreshOrgs();
      bumpImageVersion();
    } catch (error) {
      showNotificationGlobal(message(error, "Image upload failed"), "error");
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = async () => {
    try {
      await removeOrgImage(org.org_id);
      await refreshOrgs();
      bumpImageVersion();
    } catch (error) {
      showNotificationGlobal(message(error, "Image removal failed"), "error");
    }
  };

  const openLeave = async () => {
    setLeavePreview(null);
    setLeaveOpen(true);
    try {
      setLeavePreview(await fetchLeaveOrgPreview(org.org_id));
    } catch (error) {
      setLeaveOpen(false);
      showNotificationGlobal(message(error, "Could not check"), "error");
    }
  };

  /// The leaver is still an owner here, so the card is still theirs to replace.
  const openCardForm = async () => {
    const transaction = await mintCardTransaction(org.org_id);
    if (!transaction) {
      showNotificationGlobal("The card form could not be opened.", "error");
      return;
    }
    setCardTransaction(transaction);
  };

  const [cardTransaction, setCardTransaction] = useState<string | null>(null);

  const confirmLeave = async () => {
    try {
      await leaveOrg(org.org_id);
      showNotificationGlobal(`You left ${org.name}`, "success");
      setLeaveOpen(false);
      await refreshOrgs();
      if (activeOrgId === org.org_id) {
        const fallback = useOrgContextStore
          .getState()
          .orgs.find((o) => o.is_personal);
        if (fallback) await switchOrg(fallback.org_id);
      }
      navigate("/overview");
    } catch (error) {
      showNotificationGlobal(message(error, "Could not leave"), "error");
    }
  };

  const confirmDelete = async () => {
    try {
      const { purge_at } = await deleteOrg(org.org_id);
      showNotificationGlobal(
        `"${org.name}" will be deleted on ${formatDayMonthYear(purge_at)}. Any admin can cancel until then.`,
        "success",
      );
      setDeleteOpen(false);
      setDeleteConfirmName("");
      // The schedule fans out to every space in the org, so the rail is stale
      // until it re-reads — same on the way back in `undoDeletion`.
      await Promise.all([refreshOrgs(), refreshSpaces()]);
    } catch (error) {
      showNotificationGlobal(message(error, "Delete failed"), "error");
    }
  };

  const undoDeletion = async () => {
    setIsCancellingDeletion(true);
    try {
      await cancelOrgDeletion(org.org_id);
      showNotificationGlobal(`Deletion of "${org.name}" cancelled`, "success");
      await Promise.all([refreshOrgs(), refreshSpaces()]);
    } catch (error) {
      showNotificationGlobal(
        message(error, "Could not cancel the deletion"),
        "error",
      );
    } finally {
      setIsCancellingDeletion(false);
    }
  };

  const closesOn = org.deletion_scheduled_at
    ? org.purge_at
      ? formatDayMonthYear(org.purge_at)
      : purgeDate(org.deletion_scheduled_at)
    : "";

  return (
    <>
      <section className="flex flex-col">
        <SectionRule label="Identity" />
        <div className="flex items-start gap-4 px-2 py-2">
          {/* The tile IS the button — hover reveals the affordance. */}
          <button
            aria-label="Change organization image"
            disabled={isUploading || !canGovern}
            onClick={() => fileInputRef.current?.click()}
            className="group relative shrink-0 cursor-pointer"
          >
            <OrgMark org={org} size="lg" />
            {canGovern && (
              <span
                className="absolute inset-0 flex items-center justify-center rounded-full opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                style={{
                  background:
                    "color-mix(in srgb, var(--color-inverse-bg) 65%, transparent)",
                  color: "var(--color-inverse-text)",
                  transitionDuration: "var(--duration-micro)",
                  ...(isUploading ? { opacity: 1 } : {}),
                }}
              >
                {isUploading ? (
                  <Spinner size="sm" />
                ) : (
                  <CameraAltOutlinedIcon sx={{ fontSize: 20 }} />
                )}
              </span>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={IMAGE_ACCEPT_STRING}
            className="hidden"
            onChange={(e) => void uploadImage(e)}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="flex min-h-8 items-center gap-2">
              <span className="min-w-0 flex-1 text-fg-title">
                <EditableName
                  name={org.name}
                  isEditing={isEditingName}
                  canEdit={isGovernor || org.is_personal}
                  editTrigger="icon"
                  ariaLabel="Organization name"
                  setIsEditing={setIsEditingName}
                  update={() => {}}
                  commit={commitName}
                  abort={() => setIsEditingName(false)}
                />
              </span>
              <Badge
                label={org.is_personal ? "Personal" : "Company"}
                className="shrink-0"
              />
            </span>
            {canGovern && (
              <span className="text-fg-muted-regular-small">
                PNG, JPG or SVG · up to 1 MB · The organization initial is used
                when no image is set.
              </span>
            )}
            {org.has_image && canGovern && (
              <span>
                <Button
                  variant="quiet-link"
                  size="xs"
                  onClick={() => void removeImage()}
                >
                  Remove image
                </Button>
              </span>
            )}
          </div>
        </div>
        <div className="flex min-h-8 items-center gap-2 px-2">
          <span className="w-33 shrink-0 text-fg-muted-medium">
            Organization ID
          </span>
          <span
            className="font-mono text-fg-muted"
            style={{ fontSize: "var(--font-size-small)" }}
          >
            {org.org_id}
          </span>
        </div>
        <div className="flex min-h-8 items-center gap-2 px-2">
          <span className="w-33 shrink-0 text-fg-muted-medium">
            MCP endpoint
          </span>
          <Tooltip title="Connections made with this URL are confined to this organization's data">
            <span
              className="truncate select-text font-mono text-fg-muted"
              style={{ fontSize: "var(--font-size-small)" }}
            >
              {mcpEndpointUrl}
            </span>
          </Tooltip>
          <CopyButton text={mcpEndpointUrl} ariaLabel="Copy MCP endpoint" />
        </div>
        <div className="flex min-h-8 items-center gap-2 px-2">
          <span className="w-33 shrink-0 text-fg-muted-medium">Created</span>
          <span className="text-fg-regular">
            {formatDayMonthYear(org.created_at)}
          </span>
        </div>
        {owners.length > 0 && (
          <div className="flex min-h-8 gap-2 px-2 py-1">
            <span className="w-33 shrink-0 pt-0.5 text-fg-muted-medium">
              {owners.length > 1 ? "Owners" : "Owner"}
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              {owners.map((o) => (
                <span key={o.client_id} className="truncate text-fg-regular">
                  {o.display_name || o.email}
                  {o.email && o.display_name && (
                    <span className="pl-2 text-fg-muted-regular-small">
                      {o.email}
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
        {!org.is_personal && owners.length === 1 && (
          <div className="flex gap-2 px-2 pb-1">
            <span className="w-33 shrink-0" />
            <span className="text-fg-muted-regular-small">
              A second owner keeps the organization reachable if this one is not
              — and is required before that owner can close their account.
            </span>
          </div>
        )}
      </section>

      {org.is_personal && (canCreateOrg || companyOrgs.length > 0) && (
        <section className="flex flex-col">
          <SectionRule
            label="Organizations"
            count={companyOrgs.length > 0 ? companyOrgs.length : undefined}
            end={
              canCreateOrg && (
                <Tooltip
                  title={
                    orgCreateRefusal(orgLimits) ??
                    "A separate roster, plan and set of spaces."
                  }
                >
                  <span className="inline-flex">
                    <MetaAddButton
                      label="Create a company organization"
                      disabled={
                        orgLimits ? !orgLimits.can_create_free_org : true
                      }
                      onClick={() => navigate("/organizations/new")}
                    />
                  </span>
                </Tooltip>
              )
            }
          />
          {companyOrgs.length === 0 ? (
            <p className="max-w-prose px-2 pb-3 text-fg-muted-regular-small">
              A personal organization has no roster. Create a company
              organization to collaborate with others; your personal
              organization is unaffected.
            </p>
          ) : (
            companyOrgs.map((other) => (
              <div
                key={other.org_id}
                className="flex items-center gap-2 rounded-app px-2 item-height hover:bg-list-hover"
              >
                <OrgMark org={other} size="sm" />
                <span className="min-w-0 truncate text-fg-medium">
                  {other.name}
                </span>
                <Badge label={roleLabel(other.role)} className="shrink-0" />
                {isGovernorOf(other) && (
                  <span className="ml-auto flex items-center">
                    <TooltipButton
                      title="Settings"
                      aria-label={`Settings for ${other.name}`}
                      variant="contextual"
                      size="xs"
                      onClick={() => navigate(`/organizations/${other.org_id}`)}
                    >
                      <SettingsOutlinedIcon fontSize="inherit" />
                    </TooltipButton>
                  </span>
                )}
              </div>
            ))
          )}
        </section>
      )}

      <section className="flex flex-col">
        <SectionRule label="Danger" />
        {org.deletion_scheduled_at ? (
          <div className="flex items-center gap-3 px-2 py-2">
            <span className="min-w-0 flex-1 text-fg-regular">
              {org.deletion_started_at
                ? "The teardown is running now. Everything in this organization is on its way out, and it can no longer be stopped."
                : org.is_personal
                  ? `Goes with your account on ${closesOn}. Every space and model in it is in the Trash until then; cancel the account deletion in Settings and all of it comes back.`
                  : `Scheduled for deletion on ${closesOn}. Every space and model in it is in the Trash until then, and nothing new can be added. Cancelling brings all of it back.`}
            </span>
            {!org.is_personal && (
              <TooltipButton
                title={
                  org.deletion_started_at
                    ? "The teardown has started — there is nothing left to cancel."
                    : isGovernor
                      ? "Cancel deletion — the organization goes back to normal."
                      : "Only an owner or admin can cancel."
                }
                aria-label="Cancel deletion"
                variant="contextual"
                size="xs"
                loading={isCancellingDeletion}
                disabled={
                  !isGovernor ||
                  isCancellingDeletion ||
                  !!org.deletion_started_at
                }
                onClick={() => void undoDeletion()}
              >
                <SettingsBackupRestoreIcon fontSize="inherit" />
              </TooltipButton>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {/* Offered to an owner too: co-ownership (D46) means most owners
                CAN leave, and the last one is told why they cannot inside the
                dialog rather than by a dead button. */}
            {!org.is_personal && (
              <TooltipButton
                title="Leave the organization. Your empty spaces are deleted; spaces with other managers are retained; any you solely manage are left for a governor to reassign."
                variant="secondary"
                className={DANGER_BUTTON_WIDTH}
                onClick={() => void openLeave()}
              >
                Leave
              </TooltipButton>
            )}
            <TooltipButton
              title={
                org.is_personal
                  ? "A personal organization is deleted with its account, in Settings."
                  : isOwner
                    ? "Moves the organization and everything in it to the Trash for 30 days."
                    : "Only the owner can delete the organization."
              }
              variant="destructive"
              className={DANGER_BUTTON_WIDTH}
              disabled={!isOwner || org.is_personal}
              onClick={() => void openDeleteDialog()}
            >
              Delete
            </TooltipButton>
          </div>
        )}
      </section>

      <Dialog
        open={deleteOpen}
        title={`Delete "${org.name}"?`}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteConfirmName("");
        }}
      >
        <span className="block text-fg-muted-regular">
          {analysis
            ? "This schedules the whole organization for deletion:"
            : "This schedules the organization and everything in it for deletion."}
        </span>
        {analysis && (
          <div className="flex flex-col gap-3 pt-1">
            <ImpactRow
              icon={<LayersOutlinedIcon sx={{ fontSize: 16 }} />}
              count={analysis.spaces.length}
              label={plural(analysis.spaces.length, "space")}
            />
            <ImpactRow
              icon={<DashboardOutlinedIcon sx={{ fontSize: 16 }} />}
              count={analysis.models.length}
              label={plural(analysis.models.length, "model")}
            />
            <ImpactRow
              icon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
              count={analysis.total_tables}
              label={plural(analysis.total_tables, "table")}
            />
            <ImpactRow
              icon={<PeopleOutlinedIcon sx={{ fontSize: 16 }} />}
              count={analysis.member_count}
              label={`${plural(analysis.member_count, "member")} removed`}
              hint={
                analysis.contributors.length > 0
                  ? "Their work goes with it:"
                  : undefined
              }
            >
              {analysis.contributors.length > 0 && (
                <div className="flex flex-col pt-1">
                  {analysis.contributors.slice(0, 3).map((c) => (
                    <span
                      key={c.client_id}
                      className="flex items-center gap-2 item-height"
                    >
                      <span className="min-w-0 flex-1 truncate text-fg-regular">
                        {c.display_name}
                      </span>
                      <span className="shrink-0 text-fg-muted-regular-small">
                        {c.table_count} {plural(c.table_count, "table")}
                      </span>
                    </span>
                  ))}
                  {analysis.contributors.length > 3 && (
                    <span className="text-fg-muted-regular-small">
                      and {analysis.contributors.length - 3} other
                      {analysis.contributors.length - 3 === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              )}
            </ImpactRow>
            {analysis.critical_tables > 0 && (
              <ImpactRow
                tone="warning"
                icon={<WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />}
                count={analysis.critical_tables}
                label={`${plural(analysis.critical_tables, "table")} imported by other organizations`}
                hint={
                  criticalTables.length > 0
                    ? "Those models lose their data. Move a table into a model you keep and it survives in full."
                    : "Those models lose their data when the window closes."
                }
              >
                {criticalTables.length > 0 && (
                  <div className="flex max-h-40 flex-col overflow-y-auto">
                    {criticalTables.map((t) => (
                      <CriticalTableRow
                        key={t.table_id}
                        table={t}
                        onMove={() => setMovingTable(t)}
                      />
                    ))}
                  </div>
                )}
              </ImpactRow>
            )}
          </div>
        )}
        <span className="block text-fg-muted-regular">
          Nothing is destroyed today. Every space and model goes to the Trash
          for {GRACE_DAYS} days, the organization takes nothing new, and any
          owner or admin can cancel — everything comes back together.
        </span>
        <span className="block text-fg-regular">
          Type <span className="font-medium">{org.name}</span> to confirm.
        </span>
        <TextInput
          value={deleteConfirmName}
          onChange={setDeleteConfirmName}
          ariaLabel="Type the organization name to confirm deletion"
          autoFocus
        />
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => {
                setDeleteOpen(false);
                setDeleteConfirmName("");
              },
            },
            {
              label: "Schedule deletion",
              variant: "primary",
              disabled: deleteConfirmName.trim() !== org.name,
              onClick: () => void confirmDelete(),
            },
          ]}
        />
      </Dialog>

      {movingTable && (
        <MoveTableDialog
          tableId={movingTable.table_id}
          tableName={movingTable.table_name}
          open
          onOpenChange={(next) => {
            if (next) return;
            setMovingTable(null);
            // A moved table leaves the org, so the counts above are now stale.
            void getOrgDeletionAnalysis(org.org_id)
              .then(setAnalysis)
              .catch(() => {});
          }}
        />
      )}

      <Dialog
        open={leaveOpen}
        title={`Leave "${org.name}"?`}
        onClose={() => setLeaveOpen(false)}
      >
        {leavePreview === null ? (
          <span className="text-fg-muted-regular">
            Checking the effects of leaving…
          </span>
        ) : (
          <div className="flex flex-col gap-2">
            {leavePreview.is_owner_blocked && (
              <Callout
                variant="warning"
                title="You are this organization's only owner"
              >
                <span className="flex flex-col items-start gap-2">
                  An organization with no owner cannot be administered or
                  recovered, so the last owner cannot leave. Make someone else
                  an owner in Members and leaving is open again — or delete the
                  organization.
                </span>
              </Callout>
            )}
            {leavePreview.pays_for_org && (
              <Callout
                variant="warning"
                title="This organization's subscription is on your card"
              >
                <span className="flex flex-col items-start gap-2">
                  <span>
                    Leaving does not move it: it keeps charging you until
                    another owner replaces the card. Replace it before you leave
                    if that is not what you want.
                  </span>
                  <TooltipButton
                    title="Replace the card this subscription is charged on. Opens here."
                    variant="secondary"
                    size="sm"
                    onClick={() => void openCardForm()}
                    data-testid="leave-update-card"
                  >
                    Update card
                  </TooltipButton>
                </span>
              </Callout>
            )}
            {leavePreview.spaces_to_orphan.length > 0 && (
              <Callout
                variant="warning"
                title="These spaces lose their manager"
              >
                <span className="flex flex-col gap-1">
                  <span className="text-fg-medium">
                    {leavePreview.spaces_to_orphan.join(", ")}
                  </span>
                  You are their only manager. Nobody will own the models inside
                  until an admin appoints someone — reassign first if you can.
                </span>
              </Callout>
            )}
            {leaveBlocked(leavePreview) ? null : (
              <>
                <span className="text-fg-regular">
                  You will lose access to all content in this organization.
                </span>
                {leavePreview.spaces_to_delete.length > 0 && (
                  <span className="text-fg-regular">
                    Your empty{" "}
                    {leavePreview.spaces_to_delete.length === 1
                      ? "space"
                      : "spaces"}{" "}
                    <span className="text-fg-medium">
                      {leavePreview.spaces_to_delete.join(", ")}
                    </span>{" "}
                    will be deleted.
                  </span>
                )}
                {leavePreview.grants_to_revoke.length > 0 && (
                  <span className="text-fg-regular">
                    Your access to{" "}
                    <span className="text-fg-medium">
                      {leavePreview.grants_to_revoke.join(", ")}
                    </span>{" "}
                    will be revoked; other members keep theirs.
                  </span>
                )}
              </>
            )}
          </div>
        )}
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => setLeaveOpen(false),
            },
            {
              label: "Leave organization",
              variant: "primary",
              disabled: leavePreview === null || leaveBlocked(leavePreview),
              onClick: () => void confirmLeave(),
            },
          ]}
        />
      </Dialog>
      <CardFormDialog
        transactionId={cardTransaction}
        onClose={() => setCardTransaction(null)}
        onCompleted={() => setCardTransaction(null)}
        onSettled={() => undefined}
      />
    </>
  );
};

/// An ownerless org is unrecoverable, so that still refuses. A stranded space
/// does not: a governor can reassign it (D49).
const leaveBlocked = (preview: LeaveOrgPreview) => preview.is_owner_blocked;
